// Owner: S1 (views & lists), views-grid (wave 2). Saved views, view config (columns, footer
// aggregates, frozen columns, row height, conditional rules, grouping), the layout configs of
// views-layouts (card designer, board, calendar, timeline; `./view-layouts`) and bulk actions DTOs.
// Import as `@dataroom/core/views`.
import { z } from 'zod';
import { FieldPath, type FilterNode, FilterNodeSchema, SortSpec } from './filter';
import { Uuid } from './ids';
import { localizedName } from './localized-storage';
import { localizedText } from './localized-text';
import type { ModelIndex } from './model-index';
import type { ObjectType } from './metamodel';
import { BoardConfig, CalendarConfig, CardConfig, TimelineConfig } from './view-layouts';
import { foldForKey } from './key-fold';

export * from './view-layouts';

/** Layout types of a view (ViewSettings.dc.html layout grid). */
export const VIEW_TYPES = [
  'table',
  'board',
  'calendar',
  'timeline',
  'gallery',
  'list',
  'map',
  'form',
] as const;
export const ViewType = z.enum(VIEW_TYPES);
export type ViewType = z.infer<typeof ViewType>;

/**
 * Layouts a member can create or switch to. `map` stays readable for stored views but is not offered
 * (no map tiles in v1). `form` is retired: forms belong to their object (object settings › Forms);
 * the `data-model-forms-views` data migration removes stored form views and new ones are refused.
 */
export const CREATABLE_VIEW_TYPES = ['table', 'board', 'gallery', 'list', 'calendar', 'timeline'] as const;

/** A layout a view can be saved with or switched to: every type but the retired `form`. */
const WritableViewType = ViewType.refine((type): boolean => type !== 'form', {
  error: 'Forms are not a view layout; they belong to the object (object settings › Forms)',
});

/** Who can see a view: only the owner, one team, or the whole workspace. */
export const ViewVisibility = z.enum(['private', 'team', 'workspace']);
export type ViewVisibility = z.infer<typeof ViewVisibility>;

/** Url-safe view key used in `?view=<key>` (kebab case). */
export const ViewKey = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, { error: 'Use lower-kebab-case' });

/**
 * Footer aggregate of a grid column (and its per-group value). `range` shows min – max; the server
 * computes it from `min` and `max`. Which ones a field allows comes from its codec
 * (`aggregatesOf`), plus `count_unique` and `percent_filled` for every field and `median` wherever
 * `avg` is allowed. None by default (OV-09).
 */
export const GRID_AGGREGATES = [
  'sum',
  'avg',
  'median',
  'min',
  'max',
  'range',
  'count_filled',
  'count_empty',
  'percent_filled',
  'count_unique',
] as const;
export const GridAggregate = z.enum(GRID_AGGREGATES);
export type GridAggregate = z.infer<typeof GridAggregate>;

/** Column widths a member can drag to (plan §3.3: min 64, max 800). */
export const COLUMN_MIN_WIDTH = 64;
export const COLUMN_MAX_WIDTH = 800;

/** One column of a table or one field on a board card. */
export const ViewColumn = z
  .object({
    field: FieldPath,
    visible: z.boolean().default(true),
    width: z.number().int().min(48).max(COLUMN_MAX_WIDTH).optional(),
    /** Footer aggregate (grids); absent = none. */
    aggregate: GridAggregate.optional(),
  })
  .strict();
export type ViewColumn = z.infer<typeof ViewColumn>;

/** Row heights of the grid: 32 / 40 / 64 / 96 px; tall and extra tall wrap text. */
export const ROW_HEIGHTS = { compact: 32, default: 40, tall: 64, extra: 96 } as const;
export const RowHeight = z.enum(['compact', 'default', 'tall', 'extra']);
export type RowHeight = z.infer<typeof RowHeight>;

/** Date buckets for grouping by a date or date-time field. */
export const GroupBucket = z.enum(['day', 'week', 'month', 'quarter', 'year']);
export type GroupBucket = z.infer<typeof GroupBucket>;

/** Tones of conditional rules (badges, row and cell colours); the UI maps them to tokens. */
export const RuleTone = z.enum(['neutral', 'info', 'success', 'warning', 'danger', 'accent']);
export type RuleTone = z.infer<typeof RuleTone>;

/**
 * A conditional rule (plan §7.3): records matching `filter` get a badge (`display: 'badge'`,
 * views-layouts), a tinted row (`row`) or a tinted cell of `field` (`cell`). Evaluated on the server
 * for the visible window (`POST /objects/:key/query` `rules`); the first matching rule wins per row
 * and per cell. `text` may use `{{days_since(field)}}` (badges).
 */
export const ConditionalRule = z
  .object({
    id: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[A-Za-z0-9_-]+$/),
    label: localizedText({ maxLength: 80 }).optional(),
    filter: FilterNodeSchema,
    display: z.enum(['badge', 'row', 'cell']),
    field: FieldPath.optional(),
    tone: RuleTone,
    text: localizedText({ maxLength: 120 }).optional(),
  })
  .strict()
  .refine((r) => r.display !== 'cell' || Boolean(r.field), {
    message: 'A cell rule needs a field',
    path: ['field'],
  });
export type ConditionalRule = z.infer<typeof ConditionalRule>;
export const RULES_MAX = 30;

/** Group / column total shown in group headers, board columns and the footer. */
export const ViewTotal = z
  .object({
    fn: z.enum(['count', 'sum', 'avg']),
    field: FieldPath.optional(),
  })
  .strict()
  .refine((t) => t.fn === 'count' || Boolean(t.field), { message: 'sum and avg need a field' });
export type ViewTotal = z.infer<typeof ViewTotal>;

const configFields = {
  filter: FilterNodeSchema.nullable(),
  sort: z.array(SortSpec).max(5),
  /** Table grouping / board columns (a status, select, user, boolean or single relation field). */
  groupBy: FieldPath.nullable(),
  /** Table columns in display order. Empty = all fields. */
  columns: z.array(ViewColumn).max(200),
  /** Board / gallery card fields in display order (title is always shown). */
  cardFields: z.array(ViewColumn).max(50),
  /** Aggregate per group / column / footer (default: count). */
  total: ViewTotal.nullable(),
  /** Numeric field (probability) that weights `total` into a weighted sum. */
  weightBy: FieldPath.nullable(),
  /** Group keys (as strings, `__none__` for empty) that are collapsed in the table. */
  collapsedGroups: z.array(z.string().max(200)).max(500),
  /** Group keys whose board column / table group is hidden (e.g. the Lost stage). */
  hiddenGroups: z.array(z.string().max(200)).max(500),
  /** Hide board columns / table groups without records. */
  hideEmptyGroups: z.boolean(),
  /**
   * Boards grouped by a status: the closed columns (won / lost) only show records whose close date
   * is within the last N days (null = every closed record). Open columns are not affected.
   */
  closedWithinDays: z.number().int().min(1).max(3650).nullable(),
  /** Rows per page (table) or cards per column page (board). */
  pageSize: z.number().int().min(3).max(200),
  /** Static list: a table view whose filter is `id in [...]` maintained by "Add to list". */
  list: z.boolean(),
  /** Grid row height. */
  rowHeight: RowHeight,
  /** Grid: visible columns after the title that stay pinned while scrolling sideways. */
  frozenColumns: z.number().int().min(0).max(20),
  /** Conditional rules (row and cell colours in grids, badges on cards). */
  rules: z.array(ConditionalRule).max(RULES_MAX),
  /** Bucket of a date `groupBy` (null = by day for dates). */
  groupBucket: GroupBucket.nullable(),
  /** Card designer of board, gallery, list and the phone list (views-layouts). */
  card: CardConfig.nullable(),
  /** Board options: manual order, WIP limits, column order, quick add (views-layouts). */
  board: BoardConfig.nullable(),
  /** Calendar field mapping (views-layouts). */
  calendar: CalendarConfig.nullable(),
  /** Timeline field mapping (views-layouts). */
  timeline: TimelineConfig.nullable(),
};

export const ViewConfig = z
  .object({
    filter: configFields.filter.default(null),
    sort: configFields.sort.default([]),
    groupBy: configFields.groupBy.default(null),
    columns: configFields.columns.default([]),
    cardFields: configFields.cardFields.default([]),
    total: configFields.total.default(null),
    weightBy: configFields.weightBy.default(null),
    collapsedGroups: configFields.collapsedGroups.default([]),
    hiddenGroups: configFields.hiddenGroups.default([]),
    hideEmptyGroups: configFields.hideEmptyGroups.default(false),
    closedWithinDays: configFields.closedWithinDays.default(null),
    pageSize: configFields.pageSize.default(50),
    list: configFields.list.default(false),
    rowHeight: configFields.rowHeight.default('default'),
    frozenColumns: configFields.frozenColumns.default(0),
    rules: configFields.rules.default([]),
    groupBucket: configFields.groupBucket.default(null),
    card: configFields.card.default(null),
    board: configFields.board.default(null),
    calendar: configFields.calendar.default(null),
    timeline: configFields.timeline.default(null),
  })
  .strict();
export type ViewConfig = z.infer<typeof ViewConfig>;
export type ViewConfigInput = z.input<typeof ViewConfig>;

/** Partial config (create / "Update view"): only the given keys are set, nothing is defaulted. */
export const ViewConfigPatch = z.object(configFields).partial().strict();
export type ViewConfigPatch = z.infer<typeof ViewConfigPatch>;

/**
 * Config keys that are part of a view's saved definition: changing one in the toolbar or the Layout
 * panel marks the view unsaved. `collapsedGroups` is personal (never dirty, never in the URL draft).
 */
export const DRAFT_KEYS = [
  'filter',
  'sort',
  'groupBy',
  'groupBucket',
  'columns',
  'cardFields',
  'total',
  'weightBy',
  'hiddenGroups',
  'hideEmptyGroups',
  'closedWithinDays',
  'pageSize',
  'rowHeight',
  'frozenColumns',
  'rules',
  'card',
  'board',
  'calendar',
  'timeline',
] as const satisfies ReadonlyArray<keyof ViewConfig>;
export type DraftKey = (typeof DRAFT_KEYS)[number];

/**
 * An unsaved view draft (`?d=` in the URL, plan §7.3): a config patch plus a layout switch. Parsed
 * strictly; an invalid draft is dropped as a whole.
 */
export const ViewDraft = ViewConfigPatch.omit({ collapsedGroups: true, list: true })
  .extend({ type: WritableViewType.optional() })
  .strict();
export type ViewDraft = z.infer<typeof ViewDraft>;

export const ViewTeam = z.object({ key: z.string(), label: z.string() });
export type ViewTeam = z.infer<typeof ViewTeam>;

export const ViewDTO = z.object({
  id: Uuid,
  objectKey: z.string(),
  objectTypeId: Uuid,
  key: z.string(),
  name: z.string(),
  type: ViewType,
  icon: z.string().nullable(),
  position: z.number().int(),
  config: ViewConfig,
  visibility: ViewVisibility,
  team: ViewTeam.nullable(),
  locked: z.boolean(),
  ownerId: z.string().nullable(),
  /** The signed-in member pinned this view to the sidebar favorites. */
  favorite: z.boolean(),
  /** May save filter / sort / column changes. */
  canEdit: z.boolean(),
  /** May change visibility, lock, rename or delete. */
  canManage: z.boolean(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ViewDTO = z.infer<typeof ViewDTO>;

/** A view name: plain, or one name per language (LocalizedText, plan §8.10). */
const ViewName = localizedName(80);
const IconName = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9_-]*$/);

export const CreateViewInput = z
  .object({
    name: ViewName,
    type: WritableViewType.default('table'),
    key: ViewKey.optional(),
    icon: IconName.nullish(),
    config: ViewConfigPatch.default({}),
    visibility: ViewVisibility.default('private'),
    teamKey: z.string().max(80).nullish(),
    locked: z.boolean().default(false),
    position: z.number().int().min(0).optional(),
  })
  .strict();
export type CreateViewInput = z.input<typeof CreateViewInput>;

export const UpdateViewInput = z
  .object({
    name: ViewName,
    type: WritableViewType,
    icon: IconName.nullable(),
    /** Replaces the given config keys (others stay). */
    config: ViewConfigPatch,
    visibility: ViewVisibility,
    teamKey: z.string().max(80).nullable(),
    locked: z.boolean(),
    expectedVersion: z.number().int().positive(),
  })
  .partial()
  .strict();
export type UpdateViewInput = z.input<typeof UpdateViewInput>;

export const DuplicateViewInput = z.object({ name: ViewName.optional() }).strict();
export type DuplicateViewInput = z.input<typeof DuplicateViewInput>;

export const ReorderViewsInput = z.object({ ids: z.array(Uuid).min(1).max(500) }).strict();
export type ReorderViewsInput = z.infer<typeof ReorderViewsInput>;

export const FavoriteViewInput = z.object({ favorite: z.boolean() }).strict();

export const TeamDTO = z.object({
  key: z.string(),
  label: z.string(),
  memberIds: z.array(z.string()),
  /** The signed-in member belongs to the team. */
  isMember: z.boolean(),
});
export type TeamDTO = z.infer<typeof TeamDTO>;

// ---------------------------------------------------------------------------------------------
// Bulk actions (bulk action bar)
// ---------------------------------------------------------------------------------------------

export const BULK_MAX = 500;

export const BulkActionInput = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('update'),
      ids: z.array(Uuid).min(1).max(BULK_MAX),
      values: z.record(z.string(), z.unknown()),
    })
    .strict(),
  z
    .object({
      action: z.literal('add_to_list'),
      ids: z.array(Uuid).min(1).max(BULK_MAX),
      /** Existing list view (id or key); omit and pass `name` to create a new list. */
      viewId: z.string().max(80).optional(),
      name: ViewName.optional(),
    })
    .strict()
    .refine((v) => Boolean(v.viewId) !== Boolean(v.name), { message: 'Pass either viewId or name' }),
  z
    .object({
      action: z.literal('enrich'),
      ids: z.array(Uuid).min(1).max(BULK_MAX),
    })
    .strict(),
  z
    .object({
      action: z.literal('run_workflow'),
      ids: z.array(Uuid).min(1).max(BULK_MAX),
      workflowId: z.string().min(1).max(80),
    })
    .strict(),
]);
export type BulkActionInput = z.input<typeof BulkActionInput>;

export const BulkActionResult = z.object({
  action: z.string(),
  affected: z.number().int(),
  /** Set for add_to_list. */
  view: ViewDTO.optional(),
});
export type BulkActionResult = z.infer<typeof BulkActionResult>;

/** Max records in a static list view. */
export const LIST_MAX_RECORDS = 2000;

// ---------------------------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------------------------

export const ExportRequest = z
  .object({
    filter: FilterNodeSchema.nullish(),
    sort: z.array(SortSpec).max(5).default([]),
    q: z.string().max(200).optional(),
    /** Columns (field api keys) in order; default: all readable fields. */
    fields: z.array(FieldPath).max(200).optional(),
    /** Only these records (selection export). */
    ids: z.array(Uuid).max(BULK_MAX).optional(),
  })
  .strict();
export type ExportRequest = z.input<typeof ExportRequest>;

export const EXPORT_MAX_ROWS = 50_000;

/** Events written by bulk actions for other slices (S7 workflows, S8 enrichment) to consume. */
export const VIEW_TOPICS = {
  enrichmentRequested: 'enrichment.requested',
  workflowRunRequested: 'workflow.run_requested',
} as const;

/** Values of `$var` in filters that the UI shows by name. */
export const FILTER_VARIABLES = {
  currentMember: 'current_member',
  currentTeam: 'current_member.team',
} as const;

// ---------------------------------------------------------------------------------------------
// Dangling references (data-model fix round)
// ---------------------------------------------------------------------------------------------

const SYSTEM_PATHS = ['id', 'title', 'version', 'created_at', 'updated_at', 'created_by', 'updated_by'];

/**
 * Whether a field key or relation path (`company.industry`) of a saved view still resolves in the model:
 * every segment is a field of the object reached so far and every segment before the last one is a relation
 * whose other end is live. A path that does not is a *dangling reference* (the field was removed, or the
 * relation points at an archived object) and would fail every query of the view.
 */
export function viewPathResolves(model: ModelIndex, object: ObjectType, path: string): boolean {
  const segments = path.split('.');
  let current: ObjectType | undefined = object;
  for (const [i, seg] of segments.entries()) {
    if (!current) return false;
    const field = model.fieldByKey(current.id, seg);
    const last = i === segments.length - 1;
    if (!field) return last && SYSTEM_PATHS.includes(seg);
    if (!last) current = model.relationTarget(field)?.target;
  }
  return Boolean(current);
}

function pruneFilter(node: FilterNode, keep: (path: string) => boolean): FilterNode | null {
  if ('field' in node) return keep(node.field) ? node : null;
  if ('not' in node) {
    const inner = pruneFilter(node.not, keep);
    return inner ? (inner === node.not ? node : { not: inner }) : null;
  }
  const children = 'and' in node ? node.and : node.or;
  const kept = children.map((c) => pruneFilter(c, keep)).filter((c): c is FilterNode => c !== null);
  if (kept.length === children.length && kept.every((c, i) => c === children[i])) return node;
  if (kept.length === 0) return null;
  return 'and' in node ? { and: kept } : { or: kept };
}

/**
 * A view config without the parts that name a field that does not resolve (`keep(path)` false): conditions of
 * the filter and of rules (a rule whose whole filter goes is dropped), sort keys, columns, card fields, the
 * grouping, the total and the weight. Returns the same object when nothing dangles; `removed` lists what went
 * (`config.rules.2`, `config.sort.0` …) for a report.
 */
export function pruneViewConfig(
  config: ViewConfig,
  keep: (path: string) => boolean,
): { config: ViewConfig; removed: string[] } {
  const removed: string[] = [];
  const next: ViewConfig = { ...config };
  if (config.filter) {
    const filter = pruneFilter(config.filter, keep);
    if (filter !== config.filter) {
      removed.push('config.filter');
      next.filter = filter;
    }
  }
  const rules: ConditionalRule[] = [];
  config.rules.forEach((rule, i) => {
    const filter = pruneFilter(rule.filter, keep);
    const cellOk = rule.display !== 'cell' || (rule.field !== undefined && keep(rule.field));
    if (!filter || !cellOk) removed.push(`config.rules.${i}`);
    else if (filter !== rule.filter) {
      removed.push(`config.rules.${i}`);
      rules.push({ ...rule, filter });
    } else rules.push(rule);
  });
  if (rules.length !== config.rules.length || rules.some((r, i) => r !== config.rules[i])) next.rules = rules;
  const sort = config.sort.filter((s, i) => keep(s.field) || (removed.push(`config.sort.${i}`), false));
  if (sort.length !== config.sort.length) next.sort = sort;
  const columns = config.columns.filter(
    (c, i) => keep(c.field) || (removed.push(`config.columns.${i}`), false),
  );
  if (columns.length !== config.columns.length) next.columns = columns;
  const cardFields = config.cardFields.filter(
    (c, i) => keep(c.field) || (removed.push(`config.cardFields.${i}`), false),
  );
  if (cardFields.length !== config.cardFields.length) next.cardFields = cardFields;
  if (config.groupBy && !keep(config.groupBy)) {
    removed.push('config.groupBy');
    next.groupBy = null;
    next.groupBucket = null;
  }
  if (config.total?.field && !keep(config.total.field)) {
    removed.push('config.total');
    next.total = null;
  }
  if (config.weightBy && !keep(config.weightBy)) {
    removed.push('config.weightBy');
    next.weightBy = null;
  }
  return removed.length ? { config: next, removed } : { config, removed };
}

/** Stable key for groups whose value is empty. */
export const EMPTY_GROUP_KEY = '__none__';

/** Teams a view can be shared with: the workspace's teams (S10), key = team id. */
export const TeamsSetting = z.array(
  z.object({
    key: z.string().min(1).max(80),
    label: z.string().min(1).max(80),
    memberIds: z.array(Uuid).max(5000),
  }),
);
export type TeamsSetting = z.infer<typeof TeamsSetting>;

/** Kebab-case key from a view name ("Stalled 14+ days" → "stalled-14-days"). */
export function viewKeyFromName(name: string): string {
  const base = foldForKey(name)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'view';
}

/**
 * The filter a board queries with: the view filter, and — with `closedWithinDays` on a board
 * grouped by a status field — only open records or records closed within the window (by the
 * object's close date field).
 */
export function boardFilter(
  model: ModelIndex,
  object: ObjectType,
  config: { filter: FilterNode | null; groupBy: string | null; closedWithinDays?: number | null | undefined },
): FilterNode | null {
  if (!config.closedWithinDays || !config.groupBy) return config.filter;
  const status = model.fieldByKey(object.id, config.groupBy);
  const close = model.fieldByRole(object.id, 'close_date');
  if (!status || status.type !== 'status' || !close) return config.filter;
  const window: FilterNode = {
    or: [
      { field: status.key, op: 'in_group', value: ['open'] },
      { field: close.key, op: 'on_or_after', value: { $rel: `-${config.closedWithinDays}d` } },
    ],
  };
  return config.filter ? { and: [config.filter, window] } : window;
}

// -- the `view.patch` AI tool (a proposal the list screen shows as ghost chips) -------------------------

/** A change of a list view: filter (added to the current one or replacing it), sort, grouping, fields. */
export const ViewPatch = z
  .object({
    /** `add`: AND the node to the current filter; `replace`: the whole new filter (null clears it). */
    filter: z
      .object({ mode: z.enum(['add', 'replace']), node: FilterNodeSchema.nullable() })
      .strict()
      .optional(),
    sort: z.array(SortSpec).max(5).optional(),
    groupBy: FieldPath.nullable().optional(),
    show: z.array(FieldPath).max(30).optional(),
    hide: z.array(FieldPath).max(30).optional(),
  })
  .strict();
export type ViewPatch = z.infer<typeof ViewPatch>;

/** The output of `view.patch` (apps/api/src/modules/views/ai-tools.ts): what the client checks before showing it. */
export const ViewPatchProposal = z.object({
  objectKey: z.string(),
  summary: z.string(),
  patch: ViewPatch,
});
export type ViewPatchProposal = z.infer<typeof ViewPatchProposal>;
